import { useEffect, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api, { getApiError } from '../../lib/axios';
import { Store, Plus, Trash2, Loader2, CheckCircle, X, Shield } from 'lucide-react';

interface AdminSeller {
  id: string;
  name: string;
  website_url: string | null;
  seller_type: string;
  contact_email: string | null;
  is_verified: boolean;
  is_active: boolean;
  listing_count: number;
}

export default function AdminSellers() {
  const [sellers, setSellers] = useState<AdminSeller[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newType, setNewType] = useState('marketplace');
  const [newEmail, setNewEmail] = useState('');

  const fetchSellers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/sellers');
      setSellers(res.data);
    } catch (err: any) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSellers(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await api.post('/api/admin/sellers', {
        name: newName, website_url: newUrl || null,
        seller_type: newType, contact_email: newEmail || null,
      });
      setSellers(prev => [...prev, res.data]);
      setShowCreate(false);
      setNewName(''); setNewUrl(''); setNewType('marketplace'); setNewEmail('');
    } catch (err: any) {
      setError(getApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const toggleVerified = async (id: string, current: boolean) => {
    try {
      await api.patch(`/api/admin/sellers/${id}`, { is_verified: !current });
      setSellers(prev => prev.map(s => s.id === id ? { ...s, is_verified: !current } : s));
    } catch (err: any) {
      setError(getApiError(err));
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete seller "${name}" and all their listings?`)) return;
    try {
      await api.delete(`/api/admin/sellers/${id}`);
      setSellers(prev => prev.filter(s => s.id !== id));
    } catch (err: any) {
      setError(getApiError(err));
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Sellers</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{sellers.length} sellers</p>
          </div>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Seller
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-brand-600" /></div>
        ) : (
          <div className="grid gap-4">
            {sellers.map(s => (
              <div key={s.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <Store className="w-5 h-5 text-green-500" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900 dark:text-white">{s.name}</h3>
                      {s.is_verified && <CheckCircle className="w-4 h-4 text-green-500" />}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {s.seller_type} &middot; {s.listing_count} listings
                      {s.website_url && (
                        <> &middot; <a href={s.website_url} target="_blank" rel="noopener noreferrer" className="text-brand-500 hover:underline">{s.website_url}</a></>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button onClick={() => toggleVerified(s.id, s.is_verified)} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700" title={s.is_verified ? 'Unverify' : 'Verify'}>
                    <Shield className={`w-4 h-4 ${s.is_verified ? 'text-green-500' : 'text-gray-400'}`} />
                  </button>
                  <button onClick={() => handleDelete(s.id, s.name)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Add Seller</h2>
                <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"><X className="w-5 h-5 text-gray-500" /></button>
              </div>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
                  <input value={newName} onChange={e => setNewName(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-sm" placeholder="e.g. Adsy" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Website URL</label>
                  <input value={newUrl} onChange={e => setNewUrl(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-sm" placeholder="https://adsy.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                  <select value={newType} onChange={e => setNewType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-sm">
                    <option value="marketplace">Marketplace</option>
                    <option value="direct">Direct</option>
                    <option value="agency">Agency</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Email</label>
                  <input value={newEmail} onChange={e => setNewEmail(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-sm" placeholder="contact@adsy.com" />
                </div>
                <button type="submit" disabled={creating || !newName} className="w-full py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {creating ? 'Creating...' : 'Add Seller'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
